import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from './icons/index.tsx';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  icon?: IconName;
  /** Shows a spinner and blocks clicks while keeping the button focusable. */
  loading?: boolean;
  /** Visual "chosen" state (also exposed as aria-pressed when set). */
  selected?: boolean;
  block?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    loading = false,
    selected,
    block = false,
    className,
    children,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    styles['button'],
    styles[variant],
    styles[size],
    block ? styles['block'] : '',
    loading ? styles['loading'] : '',
    selected ? styles['selected'] : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      aria-pressed={selected}
      aria-busy={loading || undefined}
      onClick={loading ? undefined : onClick}
      {...rest}
    >
      {loading ? <span className={styles['spinner']} aria-hidden="true" /> : null}
      {icon && !loading ? <Icon name={icon} size="1.25em" /> : null}
      {children ? <span className={styles['label']}>{children}</span> : null}
    </button>
  );
});
